// src/services/parentService.js
import api from './api';

// ============================================
// HELPER FUNCTIONS (These stay exactly the same)
// ============================================

export const getEndOfMonthDate = () => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return lastDay.toISOString().split('T')[0];
};

export const calculateMonthlyPayment = (hourlyRate, sessionsPerWeek, daysPerWeek, sessionDuration, startDate) => {
  if (!hourlyRate || !sessionsPerWeek || !daysPerWeek || !sessionDuration || !startDate) {
    return { monthlyHours: 0, monthlyCost: 0 };
  }

  const start = new Date(startDate);
  const today = new Date();
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  const effectiveStart = start > today ? start : today;
  
  const timeDiff = endOfMonth.getTime() - effectiveStart.getTime();
  const remainingDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  if (remainingDays <= 0) {
    return { monthlyHours: 0, monthlyCost: 0 };
  }
  
  const remainingWeeks = remainingDays / 7;
  const hoursPerSession = sessionDuration / 60;
  const hoursPerWeek = hoursPerSession * sessionsPerWeek;
  const totalHours = hoursPerWeek * remainingWeeks;
  const totalCost = totalHours * hourlyRate;
  
  return {
    monthlyHours: parseFloat(totalHours.toFixed(2)),
    monthlyCost: parseFloat(totalCost.toFixed(2))
  };
};

export const getTRCNStatus = (teacher) => {
  if (teacher.trcnNumber && teacher.trcnVerified === true) {
    return { status: 'verified', text: 'TRCN Verified', badgeClass: 'bg-success' };
  } else if (teacher.trcnNumber && teacher.trcnVerified === false) {
    return { status: 'pending', text: 'TRCN Pending Verification', badgeClass: 'bg-warning' };
  } else {
    return { status: 'not_verified', text: 'TRCN Not Verified', badgeClass: 'bg-secondary' };
  }
};

// ============================================
// API FUNCTIONS - Using your backend
// ============================================

// Wallet & Transactions
export const fetchWallet = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/wallet`);
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet:', error);
    throw error;
  }
};

export const fetchTransactions = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/transactions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

export const fetchMonthlyInvoices = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/invoices`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

// Teachers
export const fetchTeachers = async (filters = {}) => {
  try {
    console.log('Fetching teachers with filters:', filters);

    const params = new URLSearchParams();

if (filters.state && filters.state !== '') {
  params.append('state', filters.state);
}

if (filters.localGovernment && filters.localGovernment !== '') {
  params.append('localGovernment', filters.localGovernment);
}

if (filters.email && filters.email !== '') {
  params.append('email', filters.email);
}

const queryString = params.toString();

console.log("📡 Parent query:", queryString);

const response = await api.get(`/teachers?${queryString}`);

    const teachers = (response.data.teachers || []).map(teacher => ({
      ...teacher,
      hasTRCN: !!teacher.trcnNumber,
      trcnStatus: teacher.trcnNumber
        ? (teacher.trcnVerified === true
            ? 'verified'
            : teacher.trcnVerified === false
            ? 'pending'
            : 'not_verified')
        : 'no_trcn',
      profileLink: `/teacher-profile/${teacher.id}`
    }));

    return teachers;

  } catch (error) {
    console.error('Error fetching teachers:', error);
    return [];
  }
};

export const fetchTeacherActivity = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}/activity`);
    return response.data;
  } catch (error) {
    console.error('Error fetching teacher activity:', error);
    return { 
      history: [], 
      reviews: [], 
      totalSessions: 0, 
      averageRating: 0,
      totalReviews: 0
    };
  }
};

// Hiring Requests
export const fetchHiringRequests = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/hiring-requests`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hiring requests:', error);
    throw error;
  }
};

// Children Management
export const fetchChildren = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/children`);
    return response.data;
  } catch (error) {
    console.error('Error fetching children:', error);
    throw error;
  }
};

export const registerChild = async (childForm, parentId) => {
  try {
    const childData = {
      ...childForm,
      parentId: parentId,
      createdAt: new Date().toISOString(),
      status: 'active',
      progress: {
        overallPerformance: 'Not assessed',
        subjects: {},
        attendance: 0,
        assessmentsCompleted: 0
      }
    };

    const response = await api.post(`/parents/${parentId}/children`, childData);
    return response.data;
  } catch (error) {
    console.error('Error registering child:', error);
    throw error;
  }
};

// ============ NEW FUNCTIONS - AI Predictions ============
export const fetchChildPrediction = async (childId) => {
  try {
    const response = await api.get(`/children/${childId}/predictions/latest`);
    return response.data?.data || null;
  } catch (error) {
    // ✅ IGNORE 404 (feature not implemented yet)
    if (error.response?.status === 404) {
      console.warn(`⚠️ No prediction route yet for child ${childId}`);
      return null;
    }

    console.error('Error fetching child prediction:', error);
    return null;
  }
};

export const fetchStudyPlans = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/study-plans`);
    return response.data;
  } catch (error) {
    console.error('Error fetching study plans:', error);
    return [];
  }
};

export const verifyPayment = async (paymentData) => {
  try {
    const response = await api.post('/payments/verify', paymentData);
    return response.data;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

// ============ EXISTING AI Features ============
export const generateAiPrediction = async (childId, children) => {
  try {
    if (!childId || childId === '') {
      throw new Error('Please select a child first');
    }
    
    const childIndex = parseInt(childId);
    const child = children[childIndex];
    
    if (!child) {
      throw new Error('Selected child not found');
    }
    
    return {
      childName: `${child.firstName} ${child.lastName}`,
      predictedPerformance: 'Good',
      confidence: '85%',
      strengths: ['Mathematics', 'Logical Reasoning'],
      areasForImprovement: ['Reading Comprehension', 'Time Management'],
      recommendations: [
        'Increase practice sessions in reading comprehension',
        'Implement structured study schedule',
        'Consider additional tutoring in language arts'
      ]
    };
  } catch (error) {
    console.error('Error generating AI prediction:', error);
    throw error;
  }
};

export const generateStudyPlan = async (childId, subject, children) => {
  try {
    if (!childId || childId === '' || !subject) {
      throw new Error('Please select a child and enter a subject first');
    }
    
    const childIndex = parseInt(childId);
    const child = children[childIndex];
    
    if (!child) {
      throw new Error('Selected child not found');
    }
    
    return {
      childName: `${child.firstName} ${child.lastName}`,
      subject: subject,
      duration: '4 weeks',
      weeklySchedule: [
        { day: 'Monday', activities: ['Concept Introduction', 'Basic Practice'] },
        { day: 'Wednesday', activities: ['Advanced Practice', 'Problem Solving'] },
        { day: 'Friday', activities: ['Review', 'Assessment'] }
      ],
      goals: [
        'Master core concepts',
        'Improve problem-solving speed',
        'Achieve 90%+ in assessments'
      ]
    };
  } catch (error) {
    console.error('Error generating study plan:', error);
    throw error;
  }
};

// Wallet Operations
export const fundWallet = async (fundingForm, parentId) => {
  try {
    const amount = parseFloat(fundingForm.amount);
    if (isNaN(amount) || amount <= 0) {
      throw new Error('Please enter a valid amount');
    }

    const response = await api.post(`/parents/${parentId}/wallet/fund`, fundingForm);
    return response.data;
  } catch (error) {
    console.error('Error funding wallet:', error);
    throw error;
  }
};

// Payment Processing
export const processPayment = async (requestId, walletBalance, parentId, parentName) => {
  try {
    const response = await api.post(`/parents/${parentId}/payments`, {
      requestId,
      walletBalance,
      parentName
    });
    return response.data.paymentAmount;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

// Hire Teacher
export const hireTeacher = async (hireForm, children, teachers, hiringRequests, parentId, parentName) => {
  console.log("🚀 hireTeacher started");
  try {
    if (!hireForm.teacherId || !hireForm.childId || !hireForm.subject) {
  throw new Error('Please fill in required fields');
}

   const hourlyRate = parseFloat(hireForm.hourlyRate || 0);
    if (isNaN(hourlyRate) || hourlyRate < 1000) {
      throw new Error('Please enter a valid hourly rate (minimum ₦1000)');
    }

    if (!hireForm.preferredDays || hireForm.preferredDays.length === 0) {
  throw new Error('Please select at least one preferred day');
}
console.log("📦 preferredDays:", hireForm.preferredDays);
console.log("📦 sessionsPerWeek:", hireForm.sessionsPerWeek);
console.log("📦 sessionDuration:", hireForm.sessionDuration);

  let selectedChild = children.find(
  child => child.id === hireForm.childId
);

const selectedTeacher = teachers.find(
  t => t.id === hireForm.teacherId
);

if (!selectedChild) {
  console.warn("⚠️ Child not found — using fallback");

  // TEMP fallback to allow API call
  selectedChild = children[0];
}

if (!selectedTeacher) {
  throw new Error('Selected teacher not found. Please refresh the teacher list and try again.');
}
const calculation = calculateMonthlyPayment(
  hourlyRate,
  parseInt(hireForm.sessionsPerWeek),
  hireForm.preferredDays.length,
  parseInt(hireForm.sessionDuration),
  new Date()
);
console.log("📡 ABOUT TO CALL API");
let response;

try {
  console.log("📡 SENDING REQUEST NOW...");

  response = await api.post(`/parents/${parentId}/hire-teacher`, {
    teacherId: hireForm.teacherId,
    childId: selectedChild.id,
    subject: hireForm.subject,

    schedule: {
      preferredDays: hireForm.preferredDays,
      sessionsPerWeek: parseInt(hireForm.sessionsPerWeek),
      sessionDuration: parseInt(hireForm.sessionDuration),
      startDate: hireForm.startDate || new Date()
    },

    ratePerSession: hourlyRate,

    metadata: {
      childName: `${selectedChild.firstName} ${selectedChild.lastName}`,
      teacherName: selectedTeacher.name,
      parentName,
      estimatedMonthlyCost: calculation.monthlyCost
    }
  });

  console.log("✅ API RESPONSE:", response.data);

} catch (error) {
  console.error("❌ FULL AXIOS ERROR:", error);
  console.error("❌ ERROR RESPONSE:", error.response);
  console.error("❌ ERROR MESSAGE:", error.message);

  throw error;
}
    return calculation.monthlyCost;
  } catch (error) {
    console.error('Error hiring teacher:', error);
    throw error;
  }
};

// Cancel Hiring Request
export const cancelHiringRequest = async (requestId, parentId) => {
  try {
    const response = await api.post(`/parents/${parentId}/hiring-requests/${requestId}/cancel`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling hiring request:', error);
    throw error;
  }
};

// Support a Student (Donation/Sponsorship)
export const supportStudent = async (donationData, parentId, parentName) => {
  try {
    const { childId, amount, message } = donationData;
    
    if (!childId || !amount || amount <= 0) {
      throw new Error('Please provide valid child and amount');
    }

    const response = await api.post(`/parents/${parentId}/support-student`, {
      childId,
      amount,
      message,
      parentName
    });

    return response.data;
  } catch (error) {
    console.error('Error supporting student:', error);
    throw error;
  }
};

export const buyCoins = async (parentId, amount) => {
  try {
    const response = await api.post(
      `/parents/${parentId}/buy-coins`,
      { amount }
    );

    return response.data;

  } catch (error) {
    console.error("❌ Buy coins error:", error);
    throw error;
  }
};
// Support exam fees (WAEC, NECO, JAMB, Other)
export const supportExamFee = async (examData, parentId, parentName) => {
  try {
    const { supportType, numberOfStudents, amount, description, message, specificChildId } = examData;
    
    if (!supportType || !numberOfStudents || !amount || amount <= 0) {
      throw new Error('Please provide valid exam support details');
    }

    const response = await api.post(`/parents/${parentId}/support-exam-fee`, {
      supportType,
      numberOfStudents,
      amount,
      description,
      message,
      specificChildId,
      parentName
    });

    return response.data;
  } catch (error) {
    console.error('Error supporting exam fee:', error);
    throw error;
  }
};

// Get children available for support
export const getChildrenForSupport = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/supportable-children`);
    return response.data;
  } catch (error) {
    console.error('Error getting children for support:', error);
    throw error;
  }
};

// Get donation history
export const getDonationHistory = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/donations`);
    return response.data;
  } catch (error) {
    console.error('Error getting donation history:', error);
    throw error;
  }
};

// Get exam fee statistics
export const getExamFeeStats = async (parentId) => {
  try {
    const response = await api.get(`/parents/${parentId}/exam-fee-stats`);
    return response.data;
  } catch (error) {
    console.error('Error getting exam fee stats:', error);
    return {
      totalSupported: 0,
      waecSupported: 0,
      necoSupported: 0,
      jambSupported: 0,
      otherSupported: 0
    };
  }
};

// Process direct payment for support (without wallet)
export const processDirectSupportPayment = async (paymentData, parentId, parentName) => {
  try {
    const { childId, amount, paymentMethod, email, metadata } = paymentData;
    
    if (!amount || amount <= 0 || !email) {
      throw new Error('Please provide all required payment information');
    }

    const response = await api.post(`/parents/${parentId}/direct-support-payment`, {
      childId,
      amount,
      paymentMethod,
      email,
      metadata,
      parentName
    });

    return response.data;
  } catch (error) {
    console.error('Error processing direct support payment:', error);
    throw error;
  }
};

// Auto Schedule Functions
export const generateAutoSchedule = (autoScheduleForm, children) => {
  if (!autoScheduleForm.childId || !autoScheduleForm.subject) {
    throw new Error('Please select child and subject');
  }

  const childIndex = parseInt(autoScheduleForm.childId);
  const child = children[childIndex];
  
  if (!child) {
    throw new Error('Selected child not found');
  }

  const timeSlots = {
    'morning': ['8:00 AM', '9:00 AM', '10:00 AM'],
    'afternoon': ['2:00 PM', '3:00 PM', '4:00 PM'],
    'evening': ['5:00 PM', '6:00 PM', '7:00 PM']
  };

  const selectedDays = autoScheduleForm.preferredDays && 
                      Array.isArray(autoScheduleForm.preferredDays) && 
                      autoScheduleForm.preferredDays.length > 0 
    ? autoScheduleForm.preferredDays 
    : ['Monday', 'Wednesday', 'Friday'];

  const selectedTimes = timeSlots[autoScheduleForm.preferredTimeSlot] || timeSlots['afternoon'];
  
  return selectedDays.map((day, index) => {
    const timeIndex = index % selectedTimes.length;
    const meetingId = `meet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const meetingLink = `https://meet.eduspace.com/${meetingId}`;
    
    return {
      id: Date.now() + index,
      day: day,
      time: selectedTimes[timeIndex],
      subject: autoScheduleForm.subject,
      childName: `${child.firstName} ${child.lastName}`,
      grade: child.grade,
      duration: `${autoScheduleForm.sessionDuration} minutes`,
      sessionType: 'online',
      recurring: true,
      status: 'pending',
      meetingLink: meetingLink,
      meetingId: meetingId
    };
  });
};

// Save Generated Schedule
export const saveGeneratedSchedule = async (schedule, autoScheduleForm, children, parentId, parentName) => {
  try {
    const childIndex = parseInt(autoScheduleForm.childId);
    const child = children[childIndex];
    
    if (!child) {
      throw new Error('Child data not found');
    }

    const calculateNextSessionDate = (dayOfWeek) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = new Date();
      const todayIndex = today.getDay();
      const targetIndex = days.indexOf(dayOfWeek);
      
      let daysToAdd = targetIndex - todayIndex;
      if (daysToAdd < 0) {
        daysToAdd += 7;
      } else if (daysToAdd === 0) {
        const now = today.getHours() * 60 + today.getMinutes();
        if (now >= 16 * 60) {
          daysToAdd = 7;
        }
      }
      
      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysToAdd);
      return nextDate.toISOString();
    };

    const response = await api.post(`/parents/${parentId}/schedules`, {
      schedule,
      autoScheduleForm,
      childData: {
        id: child.studentId || `child-${childIndex}`,
        name: `${child.firstName} ${child.lastName}`,
        grade: child.grade,
        email: child.email || ''
      },
      parentName,
      nextSessionDate: calculateNextSessionDate(schedule.day)
    });

    return response.data.meetingLink;
  } catch (error) {
    console.error('Error saving schedule:', error);
    throw error;
  }
};

// Accept Teacher Adjustments
export const acceptTeacherAdjustments = async (scheduleId, parentId, parentName) => {
  try {
    const response = await api.post(`/parents/${parentId}/schedules/${scheduleId}/accept`, {
      parentName
    });
    return response.data.meetingLink;
  } catch (error) {
    console.error('Error accepting adjustments:', error);
    throw error;
  }
};

// ============================================
// HELPER FUNCTION (keep for now)
// ============================================
// const calculateNeedScore = (child, performance, totalDonations) => {
//   let score = 0;
  
//   const performanceScore = 100 - (performance.overallScore || 0);
//   score += performanceScore * 0.4;
  
//   if (totalDonations === 0) {
//     score += 30;
//   } else if (totalDonations < 5000) {
//     score += 20;
//   } else if (totalDonations < 20000) {
//     score += 10;
//   }
  
//   const grade = child.grade || '';
//   if (grade.includes('Primary 1') || grade.includes('Primary 2')) {
//     score += 15;
//   } else if (grade.includes('Primary') || grade.includes('JSS')) {
//     score += 10;
//   }
  
//   if (child.specialNeeds || child.learningDisability) {
//     score += 20;
//   }
  
//   return Math.min(score, 100);
// };